import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Routes } from "app/route/RoutesConfig";
import { userSelector } from "slices/user/userSlice";
import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import { ReactComponent as Arrow } from "images/arrow-down_gray.svg";
import { useUnassignedStopNestExpandSync } from "../unassignedStopNestExpandContext";
import type { UnassignedStopGroup } from "../unassignedPickupTypes";
import "./pickupGroupCard.scss";

const groupStatusClass: Record<DispatchManifestStopDetailStatus, string> = {
  [DispatchManifestStopDetailStatus.COMPLETED]: "pa-pickup-group__tag--completed",
  [DispatchManifestStopDetailStatus.IN_PROGRESS]: "pa-pickup-group__tag--in-progress",
  [DispatchManifestStopDetailStatus.PENDING]: "pa-pickup-group__tag--pending",
  [DispatchManifestStopDetailStatus.EXCEPTIONS]: "pa-pickup-group__tag--exceptions",
};

function titleCaseWords(s: string): string {
  return s
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

function groupStatusLabel(s: DispatchManifestStopDetailStatus): string {
  return titleCaseWords(s);
}

type Props = {
  task: UnassignedStopGroup;
  onSelectPickup: (pickupNumber: string) => void;
  selectedPickupIds: Set<string>;
  onTogglePickupSelection: (pickupId: string) => void;
};

export const UnassignedPickupStopGroup: React.FC<Props> = ({
  task,
  onSelectPickup,
  selectedPickupIds,
  onTogglePickupSelection,
}) => {
  const terminalId = useSelector(userSelector).activeTerminal?.id;
  const probillHref = useCallback(
    (probillNumber: number) => {
      const prefix = terminalId != null ? `/${terminalId}` : "";
      return `${prefix}${Routes.shipments.tracking}/${probillNumber}`;
    },
    [terminalId]
  );

  const [open, setOpen] = useUnassignedStopNestExpandSync();

  const first = task.pickups[0];
  const addressLine = first?.address ?? "—";
  const windowLine = first?.timeRange ?? "—";
  const collapsedAddressWindow = `${addressLine} · ${windowLine}`;
  const pickupCount = task.pickups.length;

  return (
    <div className="pa-pickup-group">
      <button
        type="button"
        className={`pa-pickup-group__head ${!open ? "pa-pickup-group__head--single-line" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Arrow
          className="pa-pickup-group__chevron"
          width={8}
          height={12}
          color="#828282"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
        <div className="pa-pickup-group__head-main">
          {!open ? (
            <div className="pa-pickup-group__collapsed-row">
              <span className="pa-pickup-group__address pa-pickup-group__address--collapsed">
                {collapsedAddressWindow}
              </span>
              <span className="pa-pickup-group__stat-tags">
                <span className="pa-pickup-group__type-tag pa-pickup-group__type-tag--pickup">
                  <FontAwesomeIcon icon={faArrowUp} className="pa-pickup-group__fa-icon" />
                  {pickupCount}
                </span>
              </span>
            </div>
          ) : (
            <>
              <div className="pa-pickup-group__line1">
                <span className="pa-pickup-group__address">{addressLine}</span>
                <span
                  className={`pa-pickup-group__tag ${groupStatusClass[task.groupStopStatus] ?? ""}`}
                >
                  {groupStatusLabel(task.groupStopStatus)}
                </span>
              </div>
              <div className="pa-pickup-group__line2">
                <span className="pa-pickup-group__type-tag pa-pickup-group__type-tag--pickup">
                  <FontAwesomeIcon icon={faArrowUp} className="pa-pickup-group__fa-icon" />
                  {pickupCount}
                </span>
                <span className="pa-pickup-group__stats">
                  {pickupCount} {pickupCount === 1 ? "pickup" : "pickups"}
                </span>
              </div>
            </>
          )}
        </div>
      </button>

      {open && (
        <ul className="pa-pickup-group__list">
          {task.pickups.map((pickup) => (
            <li key={pickup.id} className="pa-pickup-group__item">
              <div className="pa-pickup-group__pickup-select-row">
                <label className="pa-pickup-group__pickup-check">
                  <input
                    type="checkbox"
                    checked={selectedPickupIds.has(pickup.id)}
                    onChange={() => onTogglePickupSelection(pickup.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select pickup ${pickup.puNumber}`}
                  />
                </label>
                <div className="pa-pickup-group__pickup-select-body">
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Pickup #</span>
                    <button
                      type="button"
                      className="pa-pickup-group__link-btn"
                      onClick={() => onSelectPickup(pickup.puNumber)}
                    >
                      {pickup.puNumber}
                    </button>
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">PRO#</span>
                    {pickup.probillNumber != null && pickup.probillNumber > 0 ? (
                      <a
                        className="pa-pickup-group__external-link"
                        href={probillHref(pickup.probillNumber)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {pickup.probillNumber}
                      </a>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Date</span>
                    <span>{pickup.date}</span>
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Shipper</span>
                    <span>{pickup.shipperName}</span>
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Consignee</span>
                    <span>{pickup.consigneeName}</span>
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Status</span>
                    <span className="pa-pickup-group__status-pill">{pickup.status[0] ?? "—"}</span>
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Pieces / Weight</span>
                    <span>
                      {pickup.pieces} pcs · {pickup.weight} lbs
                    </span>
                  </div>
                  <div className="pa-pickup-group__row">
                    <span className="pa-pickup-group__label">Pickup window</span>
                    <span>{pickup.timeRange}</span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
